import { Button, Chip, createStyles, Grid, IconButton, makeStyles, Theme, Typography } from "@material-ui/core";
import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { IKImage } from "imagekitio-react";
import { RootState } from "../../../../redux/rootReducer";
import { PFXCategoryItem, selectCurrency, selectItemById } from "../../../../redux/slices/templateSlice";
import { useSelector } from "react-redux";
import fallbackImage from "../../../../images/fallback_item.png";
import useTemplateStyles from "../../../../hooks/useTemplateStyles";
import { currencyFormatter, selectItemsInCart, selectUnavailable } from "../../../../redux/slices/cleverlynkSlice";
import useCleverlynkFunctions from "../../../../hooks/useCleverlynkFunctions";
import { Check, PlusOne } from "@material-ui/icons";

interface IUpsellingSection {
  item: PFXCategoryItem;
  desktop?: boolean;
}

const UpsellingSection = ({ item, desktop }: IUpsellingSection) => {
  const unavailable = useSelector(selectUnavailable);

  const { cardStyles, titleStyles } = useTemplateStyles();
  const [added, setAdded] = useState<boolean>(false);
  const { addItemToCart } = useCleverlynkFunctions();
  const shouldAnimate = useRef<boolean>(true);
  const currency = useSelector(selectCurrency);

  const handleAdd = () => {
    if (!added) {
      addItemToCart(1, [], item, () => null);
      shouldAnimate.current = false;
      setAdded(true);
    }
  };

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      "@keyframes flash": {
        "0%": {
          backgroundColor: cardStyles?.buttonColor,
        },
        "50%": {
          backgroundColor: "#51d67d",
        },
        "100%": {
          backgroundColor: cardStyles?.buttonColor,
        },
      },
      root: {
        marginTop: desktop ? -170 : "",
        backgroundColor: cardStyles?.buttonColor,
        borderRadius: desktop ? "0px 0px 0px 2px" : 9,
        boxShadow:
          "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
        padding: desktop ? "15px 20px 25px 20px" : 20,
        transition: "background-color 500ms",
        "&.animation": {
          animation: "$flash 1s ease",
        },
      },
      card: {
        backgroundColor: "white",
        borderRadius: 9,
        boxShadow:
          "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
        cursor: "pointer",
        flexWrap: "nowrap",
      },
      imageGrid: {
        borderRadius: 9,
        boxShadow: "#00000069 1pt 1pt 3px 1px",
        maxWidth: "100%",
        height: "100%",
        objectFit: "cover",
        position: "relative",
      },
      text: {
        fontFamily: titleStyles?.textFontFamily,
        color: "#1a1a1a",
        "&.price": {
          fontWeight: "bold",
          lineHeight: 1,
          fontSize: "1.05rem",
        },
        "&.description": {
          fontWeight: "light",
          fontSize: "0.8rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          msTextOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100%",
        },
        "&.name": {
          textAlign: "left",
          fontSize: "1rem",
        },
      },
      title: {
        color: cardStyles?.textButtonColor,
        width: "100%",
        paddingBottom: 20,
        fontSize: "0.9rem",
        "&.added": {
          width: "min-content",
          marginRight: 10,
          letterSpacing: 1,
          textTransform: "uppercase",
        },
      },
    })
  );

  const classes = useStyles();

  const path = useMemo(
    () =>
      item?.images && item?.images.length > 0 && item?.images[0] !== ""
        ? {
            fallback: false,
            url: item?.images[0].split("cleverlynk-imgs.s3.amazonaws.com")[1]?.replace("resized-", ""),
          }
        : { fallback: true, url: fallbackImage },
    [item?.images]
  );

  const upsellingSection = useMemo(
    () => (
      <Grid
        item
        container
        className={`${shouldAnimate.current && added ? "animation" : ""} ${classes.root}`}
        direction="column"
        id="upselling-root">
        <Grid item container justifyContent="center">
          <Typography variant="body1" className={`${added ? "added" : ""} ${classes.title}`}>
            {added ? `Agregado` : "Aprovecha y acompáñalo con"}
          </Typography>
        </Grid>
        <Grid item container className={classes.card}>
          <Grid item style={{ padding: 8 }}>
            {path.fallback ? (
              <img src={path.url} width="70px" height="70px" className={classes.imageGrid} alt="fallbackitem" />
            ) : (
              <IKImage
                id={`${item.id}-${item?.name}-image-recently`}
                key={`${item.id}-${item?.name}-image-recently`}
                className={classes.imageGrid}
                path={path.url}
                transformation={[
                  {
                    height: 70,
                    width: 70,
                  },
                ]}
                loading="lazy"
              />
            )}
          </Grid>
          <Grid item xs={9} container wrap="nowrap" alignItems="center" style={{ padding: 8 }}>
            <Grid item xs={9} style={{ overflow: "hidden" }} container direction="column">
              <Typography variant="body1" className={`name ${classes.text}`}>
                {item?.name}
              </Typography>
              <Typography variant="overline" className={`price ${classes.text}`}>
                {currencyFormatter(item?.priceWithDiscount, currency)}
              </Typography>
              <Typography variant="caption" className={`description ${classes.text}`}>
                {item?.description}
              </Typography>
            </Grid>
            <Grid item xs={3} container justifyContent="flex-end">
              <IconButton
                disabled={unavailable?.closed}
                onClick={handleAdd}
                style={{ boxShadow: "1pt 1pt 4px 1px #00000038", backgroundColor: added ? "#51d67d" : "" }}>
                {added ? <Check /> : <PlusOne />}
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    ),
    [added, path, classes, item]
  );
  return upsellingSection;
};
export default UpsellingSection;
